import { useMemo } from 'react'
import type { ModelData, ConfigurationState } from '../types'
import { generateAllOptions } from '../utils/optionGenerator'

interface ValidationResult {
  isValid: boolean
  missingFields: string[]
  missingFieldLabels: string[]
  canShowPrice: boolean
}

export const useConfigurationValidation = (
  model: ModelData | null, 
  config: ConfigurationState
): ValidationResult => {
  return useMemo(() => {
    if (!model) {
      return {
        isValid: false,
        missingFields: [],
        missingFieldLabels: [],
        canShowPrice: false
      }
    }

    const missingFields: string[] = []
    const missingFieldLabels: string[] = []

    // Always show price (no restrictions)
    const canShowPrice = true;

    // Generate actual options for current configuration to check what's visible
    const optionGroups = generateAllOptions(model, config)

    // Check each visible option group
    optionGroups.forEach(group => {
      // Skip if group is not visible
      if (group.visible === false) {
        return
      }

      // Get current value for this group
      const currentValue = config[group.id as keyof ConfigurationState]
      
      // Check if field is required and empty
      const isRequired = [
        // Standard groups
        'variant', 'glass', 'ceramic', 'handle', 'handleSide', 'bezel', 'sideGlass', // 'serviceHandleSide' - hidden
        // Vis-a-vis front groups
        'front-variant', 'front-handleSide', 'front-handle', 'front-bezel',
        // Vis-a-vis back groups  
        'back-variant', 'back-handleSide', 'back-handle', 'back-bezel'
      ].includes(group.id)
      const isEmpty = !currentValue || currentValue === ''
      
      if (isRequired && isEmpty) {
        missingFields.push(group.id)
        missingFieldLabels.push(group.title.replace(' *', '')) // Remove asterisk from title
      }
    })

    // Special handling for vis-a-vis models (if they have front/back variants)
    if (model.glassType === 'vis-a-vis') {
      const frontVariant = config['front-variant']
      const backVariant = config['back-variant']
      
      const visAVisFields = [
        { key: 'front-variant', label: 'Wariant przedni' },
        { key: 'front-bezel', label: 'Ramka przednia' },
        { key: 'back-variant', label: 'Wariant tylny' },
        { key: 'back-bezel', label: 'Ramka tylna' }
      ]

      // Add handle fields based on variant
      let serviceHandleSideRequired = false; // UKRYTE - zawsze false
      
      if (frontVariant !== 'lift') {
        // Standard variant needs both handleSide and handle
        visAVisFields.push(
          { key: 'front-handleSide', label: 'Strona uchwytu przedniego' },
          { key: 'front-handle', label: 'Uchwyt przedni' }
        )
      } else {
        // LIFT variant needs handle first
        visAVisFields.push(
          { key: 'front-handle', label: 'Uchwyt przedni' }
        )
        // UKRYTE: serviceHandleSide required only after handle is selected
        const frontHandle = config['front-handle']
        if (false && frontHandle && frontHandle !== '') { // UKRYTE - było: if (frontHandle && frontHandle !== '')
          serviceHandleSideRequired = true;
        }
      }

      if (backVariant !== 'lift') {
        // Standard variant needs both handleSide and handle
        visAVisFields.push(
          { key: 'back-handleSide', label: 'Strona uchwytu tylnego' },
          { key: 'back-handle', label: 'Uchwyt tylny' }
        )
      } else {
        // LIFT variant needs handle first
        visAVisFields.push(
          { key: 'back-handle', label: 'Uchwyt tylny' }
        )
        // UKRYTE: serviceHandleSide required only after handle is selected
        const backHandle = config['back-handle']
        if (false && backHandle && backHandle !== '') { // UKRYTE - było: if (backHandle && backHandle !== '')
          serviceHandleSideRequired = true;
        }
      }
      
      // UKRYTE: Add vis-a-vis-serviceHandleSide only once if any LIFT variant requires it
      if (false && serviceHandleSideRequired) { // UKRYTE - było: if (serviceHandleSideRequired)
        visAVisFields.push(
          { key: 'vis-a-vis-serviceHandleSide', label: 'Strona klamki serwisowej (Lift)' }
        )
      }

      visAVisFields.forEach(field => {
        const value = config[field.key as keyof ConfigurationState]
        // Skip validation if value is 'not-applicable' (for handleSide in LIFT variants)
        if (value === 'not-applicable') {
          return;
        }
        if (!value || value === '') {
          missingFields.push(field.key)
          missingFieldLabels.push(field.label)
        }
      })
    }

    return {
      isValid: missingFields.length === 0,
      missingFields,
      missingFieldLabels,
      canShowPrice: !!canShowPrice
    }
  }, [model, config])
}
